import React from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import { configureStore } from "@reduxjs/toolkit";
import { Provider } from "react-redux";
import rootReducer from "./store/reducers";
import { apiSlice } from "./store/api/apiSlice";
import * as Sentry from "@sentry/react";
import 'simplebar-react/dist/simplebar.min.css';
import './posthog/posthog';
import { sanitizeEvent, sanitizeBreadcrumb } from "./utilis/sentry-sanitization";

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore actions that contain Date objects
        ignoredActions: [
          'comparison/setComparisonRanges',
          'comparison/setRemoveComparisonRanges',
          // Main slices
          'hospital/setFilterDateRange',
          'admission/setFilterDateRange',
          'overall/setFilterDateRange',
          'deceased/setFilterDateRange',
          'communityTransfer/setFilterDateRange',
          'quickGlace/setFilterDateRange',
          // Comparison slices
          'hospitalComparison/setFilterDateRange',
          'admissionComparison/setFilterDateRange',
          'admissionComparison/setFilterWithADTFilters',
          'overallComparison/setFilterDateRange',
          'deceasedComparison/setFilterDateRange',
          'communityTransferComparison/setFilterDateRange',
        ],
        // Ignore state paths with Date objects
        ignoredPaths: [
          // Comparison ranges (array with Date objects)
          'comparison.comparisonRanges',
          // Main slices - filter objects contain startDate/endDate
          'hospital.filter',
          'admission.filter',
          'overall.filter',
          'deceased.filter',
          'communityTransfer.filter',
          'quickGlace.filter',
          // Comparison slices
          'hospitalComparison.filter',
          'admissionComparison.filter',
          'admissionComparison.filterWithADTFilters',
          'overallComparison.filter',
          'deceasedComparison.filter',
          'communityTransferComparison.filter',
        ],
      },
    }).concat(apiSlice.middleware),
  devTools: process.env.NODE_ENV !== 'production',
});

// Only initialize Sentry in production (not in local/development)
const isLocalOrDevelopment = 
  process.env.NODE_ENV === 'development' || 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.includes('localhost');

if (!isLocalOrDevelopment) {
  const sentryDsn = process.env.REACT_APP_SENTRY_URL;
  if (sentryDsn) {
    const isProduction = process.env.NODE_ENV === 'production';
    
    Sentry.init({
      dsn: sentryDsn,
      // CRITICAL: Disable default PII collection (IP, cookies, user info, headers)
      // This prevents Sentry from automatically collecting sensitive data
      sendDefaultPii: false,
      environment: process.env.REACT_APP_ENVIRONMENT || 'development',
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        }),
        // Session Replay: For healthcare/PHI systems, consider disabling entirely
        // If enabled, use maximum masking to prevent PHI capture
        new Sentry.Replay({
          // Mask all text content and inputs to prevent PII/PHI capture
          maskAllText: true,
          maskAllInputs: true,
          // Block all media (images, videos) that might contain PHI
          blockAllMedia: true,
          // Block sensitive elements from being captured
          blockSelector: '[data-sentry-block]',
          // Mask additional sensitive selectors
          mask: [
            '[data-sentry-mask]',
            'input[type="password"]',
            'input[type="email"]',
            'input[type="text"]',
            'textarea',
            '[class*="sensitive"]',
            '[id*="sensitive"]',
            '[class*="patient"]',
            '[id*="patient"]',
            '[class*="medical"]',
            '[id*="medical"]',
          ],
        }),
      ],
      // Production: Very low sample rate for PHI-adjacent apps (5%)
      // Staging: Higher sample rate for debugging (50%)
      tracesSampleRate: isProduction ? 0.05 : 0.5,
      // Session Replay: DISABLED in production for healthcare/PHI systems
      // Even with masking, Session Replay can capture sensitive data
      // Enable only if explicitly audited and approved
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Sanitize events before sending to prevent PII/PHI leakage
      beforeSend(event, hint) {
        return sanitizeEvent(event, hint);
      },
      // Sanitize breadcrumbs to prevent sensitive data in breadcrumb trail
      beforeBreadcrumb(breadcrumb, hint) {
        return sanitizeBreadcrumb(breadcrumb, hint);
      },
    });
  } else {
    console.warn("[Sentry] REACT_APP_SENTRY_URL is not set; error tracking is disabled.");
  }
}


ReactDOM.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <App />
        <ToastContainer />
      </Provider>
    </HelmetProvider>
  </React.StrictMode>,
  document.getElementById("root")
);

function sendToAnalytics(metric) {
  // const body = JSON.stringify(metric);
  // const url = "http://localhost:5003/api/analytics";

  // // Use `navigator.sendBeacon()` if available, falling back to `fetch()`
  // if (navigator.sendBeacon) {
  //   navigator.sendBeacon(url, body);
  // } else {
  //   fetch(url, { body, method: "POST", keepalive: true });
  // }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(sendToAnalytics);
